import { ComponentProps, useEffect, useState } from 'react';
import { Alert, Box, LinearProgress, TablePagination, Typography } from '@mui/material';
import { AuditEntityType } from '@sr/dto';
import { CardPaper } from 'shared/ui/card-paper';
import { formatDateTime } from 'utils/date-format-helpers';
import { useAuditLogs } from '../audit.hook';
import { getAuditActionColors } from '../utils/audit-colors';
import { AUDIT_ACTION_LABELS, buildLeadAuditActor, buildLeadAuditDetails, buildActorName, buildPayloadEntries, buildUserName, PayloadEntry } from '../utils/audit-log.helpers';

type Props = {
  entityType: AuditEntityType;
  entityId: number;
  rowsPerPageOptions?: number[];
  paperProps?: ComponentProps<typeof CardPaper>;
};

const DEFAULT_ROWS_PER_PAGE_OPTIONS = [5, 10, 20, 50];

export const AuditLogSection = ({ entityType, entityId, rowsPerPageOptions = DEFAULT_ROWS_PER_PAGE_OPTIONS, paperProps }: Props) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);

  useEffect(() => {
    setPage(0);
  }, [entityType, entityId, rowsPerPageOptions]);

  const { logs, totalItemsCount, error, isFetching, isLoading } = useAuditLogs(
    { entityType, entityId, page, itemsPerPage: rowsPerPage },
    { skip: !entityId },
  );

  const totalPages = rowsPerPage ? Math.ceil(totalItemsCount / rowsPerPage) : 0;
  const minRowsPerPage = rowsPerPageOptions[0] ?? 5;
  const paginationDisabled = totalItemsCount <= minRowsPerPage;

  useEffect(() => {
    if (totalPages && page >= totalPages) {
      setPage(Math.max(totalPages - 1, 0));
    }
  }, [totalPages, page]);

  const errorMessage = error ?? null;

  const renderContent = () => {
    if ((isLoading || isFetching) && !logs.length) {
      return (
        <Box sx={{ py: 2 }}>
          <LinearProgress />
        </Box>
      );
    }

    if (!logs.length) {
      return (
        <Typography variant="body2" color="text.secondary">
          События аудита отсутствуют.
        </Typography>
      );
    }

    return (
      <Box>
        {logs.map((log, index) => {
          if (entityType === AuditEntityType.LEAD) {
            const header = `${formatDateTime(log.createdAt)} | ${buildLeadAuditActor(log)} | ${AUDIT_ACTION_LABELS[log.action] ?? log.action}`;
            const details = buildLeadAuditDetails(log);
            return (
              <Box key={log.id} sx={{ py: 1, borderBottom: index < logs.length - 1 ? '1px solid' : 'none', borderColor: 'divider', minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600} noWrap title={header}>{header}</Typography>
                {details && <Typography variant="body2" color="text.secondary" noWrap title={details} sx={{ mt: 0.25 }}>{details}</Typography>}
              </Box>
            );
          }
          const actorName = buildActorName(log.payload?.actor) ?? buildUserName(log.user) ?? 'Система';
          const payloadEntries = buildPayloadEntries(entityType, log.payload);
          const colorScheme = getAuditActionColors(log.action);

          return (
            <Box
              key={log.id}
              sx={{
                mb: index < logs.length - 1 ? 2 : 0,
                p: 2,
                borderRadius: 2,
                backgroundColor: colorScheme.background,
                border: `1px solid ${colorScheme.border}`,
                boxShadow: '0 1px 2px rgba(15, 23, 42, 0.08)',
              }}
            >
              <Box display="flex" justifyContent="space-between" flexWrap="wrap" gap={1} alignItems="center">
                <Typography variant="subtitle2" sx={{ color: colorScheme.text }}>
                  {AUDIT_ACTION_LABELS[log.action] ?? log.action}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatDateTime(log.createdAt)}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Пользователь:{' '}
                <Typography component="span" variant="body2" color="text.primary">
                  {actorName}
                </Typography>
              </Typography>
              {payloadEntries.length > 0 && (
                <Box mt={1}>
                  {payloadEntries.map((entry) =>
                    entry.type === 'change' ? (
                      <Box key={`${log.id}-${entry.field}`} sx={{ mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          {entry.label}
                        </Typography>
                        {renderChangeValues(entry)}
                      </Box>
                    ) : (
                      <Typography key={`${log.id}-${entry.label}`} variant="body2">
                        <Typography component="span" variant="body2" color="text.secondary">
                          {entry.label}:{' '}
                        </Typography>
                        {entry.value}
                      </Typography>
                    ),
                  )}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    );
  };

  const mergedPaperProps: ComponentProps<typeof CardPaper> = {
    ...paperProps,
    sx: {
      mt: 2,
      display: 'flex',
      flexDirection: 'column',
      minWidth: 0,
      maxWidth: '100%',
      ...(paperProps?.sx ?? {}),
    },
  };

  return (
    <CardPaper {...mergedPaperProps}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Аудит
      </Typography>
      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}
      <Box sx={{ flexGrow: 1, overflowY: 'auto', overflowX: 'hidden', minWidth: 0, pr: 1, maxHeight: 320, minHeight: 160 }}>{renderContent()}</Box>
      <TablePagination
        component="div"
        count={totalItemsCount}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(_, newPage) => {
          if (paginationDisabled) return;
          setPage(newPage);
        }}
        onRowsPerPageChange={(event) => {
          if (paginationDisabled) return;
          setRowsPerPage(parseInt(event.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={rowsPerPageOptions}
        labelRowsPerPage="Записей на странице"
        backIconButtonProps={{ disabled: paginationDisabled || page === 0 }}
        nextIconButtonProps={{ disabled: paginationDisabled || page >= Math.max(totalPages - 1, 0) }}
        SelectProps={{ disabled: paginationDisabled }}
        showFirstButton
        showLastButton
      />
    </CardPaper>
  );
};

const renderChangeValues = (entry: Extract<PayloadEntry, { type: 'change' }>) => {
  const previousValue = entry.change.previous;
  const currentValue = entry.change.current;
  return (
    <Box display="flex" flexWrap="wrap" gap={2} mt={0.5}>
      <Box display="flex" alignItems="center" gap={0.5}>
        <Typography variant="caption" color="text.secondary">
          Было:
        </Typography>
        <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 500 }}>
          {previousValue}
        </Typography>
      </Box>
      <Box display="flex" alignItems="center" gap={0.5}>
        <Typography variant="caption" color="text.secondary">
          Стало:
        </Typography>
        <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 500 }}>
          {currentValue}
        </Typography>
      </Box>
    </Box>
  );
};
